import React, {useState, useRef, useCallback} from 'react';
import SignatureCanvas from 'react-signature-canvas';
import './SignatureV2Component.scss';
// import ConfirmationStatusCardComponent from "../confirmation-status-card/ConfirmationStatusCardComponent";
import {CommonService} from "../../services";
import ButtonComponent from "../core/button/ButtonComponent";
import AddIcon from "@mui/icons-material/Add";
import SelectDropdownComponent from "../core/select-dropdown/SelectDropdownComponent";
import InputComponent from "../core/input/InputComponent";


const FONT_STYLES = [
    {label: 'Cursive', font: '"Brush Script MT", cursive'},
    {label: 'Monospace', font: 'monospace'},
    {label: 'Serif', font: 'serif'},
    {label: 'Sans-serif', font: 'sans-serif'},
];


// const COLOR_OPTIONS = ['#000000', '#0000FF'];

interface SignatureComponentProps {
    onSave: (data: { type: string; signature: string | null; font?: string; color?: string }) => void;
    initialSignatureType?: 'typed' | 'draw' | 'upload';
    defaultFontStyle?: string;
    onClear?: () => void;
};


interface ConfirmationStatusCardComponentProps {
    message: React.ReactNode;
    className?: string;
    showButton?: boolean;
    onClick?: () => void;
    buttonText?: string;

}

const ConfirmationStatusCardComponent = (props: ConfirmationStatusCardComponentProps) => {
    const {message, className, showButton, onClick, buttonText}: any = props;

    const handleOnClick = useCallback((event: any) => {
        if (onClick) {
            onClick(event);
        }
    }, [onClick]);

    return <div className={`confirmation-status-card-wrapper ${className}`}>
        <div className={'confirmation-status-message'}>{message}</div>
        {
            showButton &&
            <div className={'confirmation-status-message-button'} onClick={handleOnClick}>{buttonText}</div>

        }
    </div>
}
const SignatureV2Component = (props: SignatureComponentProps) => {
    const {onSave, initialSignatureType = 'typed', defaultFontStyle = '"Brush Script MT", cursive'} = props;

    const [signatureType, setSignatureType] = useState<'draw' | 'upload' | 'typed'>(initialSignatureType);
    const [uploadedSignature, setUploadedSignature] = useState<string | null>(null);
    const [typedSignature, setTypedSignature] = useState<string>('');
    const [selectedFont, setSelectedFont] = useState<string>(defaultFontStyle);
    // const [selectedColor, setSelectedColor] = useState<string>('#000000');
    const [selectedColor] = useState<string>('#000000');
    const signaturePadRef = useRef<SignatureCanvas | null>(null);
    const hiddenFileInput = React.useRef<any>(null);
    const [isCanvasEmpty, setIsCanvasEmpty] = useState<boolean>(true); // Track if the canvas is empty

    const handleClick = useCallback(() => {
        hiddenFileInput.current.click();
    }, []);

    const clearSignature = () => {
        if (signatureType === 'draw' && signaturePadRef.current) {
            signaturePadRef.current.clear();
            setIsCanvasEmpty(true); // Update canvas empty state
        }
        setUploadedSignature(null);

        setTypedSignature('');

    };

    const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (file) {
            // Validate file type
            const allowedTypes = ['image/png', 'image/jpeg'];
            if (!allowedTypes.includes(file.type)) {
                CommonService._alert.showToast('Invalid file type/size.')
                return;
            }

            // Validate file size (20KB to 100KB)
            const fileSizeInKB = file.size / 1024;
            if (fileSizeInKB < 20 || fileSizeInKB > 100) {
                CommonService._alert.showToast('Invalid file type/size.')
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                setUploadedSignature(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };


    // Convert typed signature to image (base64)
    const convertTextToImage = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        canvas.width = 800;
        canvas.height = 180;

        let fontSize = 60;
        if (selectedFont === 'monospace') {
            fontSize = 40;
        } else if (selectedFont === 'serif' || selectedFont === 'sans-serif') {
            fontSize = 45;
        }
        ctx.font = `${fontSize}px ${selectedFont}`;
        ctx.fillStyle = selectedColor;

        // Calculate text width
        const textWidth = ctx.measureText(typedSignature).width;

        // Calculate x position to center the text
        const xPosition = (canvas.width - textWidth) / 2;

        // Draw text at the center
        ctx.fillText(typedSignature, xPosition, 100); // 100 is the y position

        return canvas.toDataURL('image/png');
    };


    const saveSignature = () => {
        if (signatureType === 'draw' && signaturePadRef.current) {
            const signatureData = signaturePadRef.current.getTrimmedCanvas().toDataURL('image/png');
            onSave({type: 'draw', signature: signatureData});
        } else if (signatureType === 'upload' && uploadedSignature) {
            onSave({type: 'upload', signature: uploadedSignature});
        } else if (signatureType === 'typed' && typedSignature) {
            const base64Image = convertTextToImage();
            onSave({type: 'typed', signature: base64Image, font: selectedFont, color: selectedColor});
        }
    };

    // Check if the signature pad is empty
    const isSignatureEmpty = useCallback(() => {
        if (signatureType === 'typed') {
            return !typedSignature.trim();
        } else if (signatureType === 'draw') {
            return isCanvasEmpty;
        } else if (signatureType === 'upload') {
            return !uploadedSignature;
        }
        return true;
    }, [isCanvasEmpty, signatureType, typedSignature, uploadedSignature]);

    // Handle the end of drawing on the canvas
    const handleCanvasEnd = useCallback(() => {
        if (signaturePadRef.current) {
            setIsCanvasEmpty(signaturePadRef.current.isEmpty()); // Update the canvas empty state
        }
    },[]);


    return (
        <div className="signature-container">
            {/* Signature Type Tabs */}
            <div className={'tab-canvas-wrapper'}>
                <div className="signature-tabs">
                    <ButtonComponent
                        variant={'text'}
                        className={`tab-button ${signatureType === 'typed' ? 'active' : ''}`}
                        handleClick={() => {
                            clearSignature();
                            setSignatureType('typed');
                        }}
                    >
                        Type
                    </ButtonComponent>
                    <ButtonComponent
                        variant={'text'}
                        className={`tab-button ${signatureType === 'draw' ? 'active' : ''}`}
                        handleClick={() => {
                            clearSignature();
                            setSignatureType('draw');
                        }}
                    >
                        Draw
                    </ButtonComponent>
                    {/* <ButtonComponent
                        variant={'text'}
                        className={`tab-button ${signatureType === 'upload' ? 'active' : ''}`}
                        handleClick={() => {
                            clearSignature();
                            setSignatureType('upload');
                        }}
                    >
                        Upload
                    </ButtonComponent> */}

                </div>
                <div className={'sign-color-wrapper'}>


                    {/* Font Selection for  Signature */}
                    {signatureType === 'typed' && (
                        <div className="typed-signature-container">
                            <div className="font-color-selection">
                                <InputComponent
                                    name={'signature'}
                                    type="text"
                                    placeholder="Type your name"
                                    value={typedSignature}
                                    onChange={(value) => setTypedSignature(value)}
                                    inputProps={{
                                        maxLength: 30,
                                    }}
                                />
                                <div className={'mrg-left-20'}>
                                    <SelectDropdownComponent
                                        name={'font'}
                                        label={'Select Font'}
                                        options={FONT_STYLES}
                                        getOptionLabel={(option: any) => option.label}
                                        valueKey={'font'}
                                        value={selectedFont && selectedFont}
                                        onUpdate={(value: any) => setSelectedFont(value)}
                                        className="font-select"
                                    />
                                </div>

                            </div>


                            <div className="signature-preview"
                                 style={{fontFamily: selectedFont, color: selectedColor}}>
                                {typedSignature || ''}
                            </div>

                        </div>
                    )}


                    {signatureType === 'draw' && (
                        <div>
                            <SignatureCanvas
                                ref={signaturePadRef}
                                penColor={selectedColor}
                                onEnd={handleCanvasEnd}
                                canvasProps={{width: 500, height: 200, className: 'sigCanvas'}}
                            />
                        </div>
                    )}


                    {signatureType === 'upload' && (
                        <div className="upload-signature">
                            <input ref={hiddenFileInput}
                                   style={{display: 'none'}}
                                   type="file"
                                   accept="image/*"
                                   onChange={handleUpload}/>
                            <ConfirmationStatusCardComponent
                                className={'mrg-bottom-20'}
                                message={' PNG and JPEG files are allowed upto 100KB and must not be less than 20KB'}/>
                            <div className={'d-flex justify-content-center align-items-center'}>
                                {!uploadedSignature &&

                                    <ButtonComponent
                                        startIcon={<AddIcon/>}
                                        color={'secondary'}
                                        handleClick={handleClick}>
                                        Upload Signature</ButtonComponent>

                                }
                                {uploadedSignature &&
                                    <img src={uploadedSignature} alt="Uploaded Signature" width="300"/>}
                            </div>
                        </div>
                    )}

                    {/*{signatureType !== 'upload' && <div className="color-options">*/}
                    {/*    <div className={'color-circle-wrapper'}>*/}
                    {/*        {COLOR_OPTIONS.map((color) => (*/}
                    {/*            <div*/}
                    {/*                key={color}*/}
                    {/*                className="color-circle"*/}
                    {/*                style={{*/}
                    {/*                    backgroundColor: color,*/}
                    {/*                    border: selectedColor === color ? '2px solid #D26060' : 'none',*/}
                    {/*                }}*/}
                    {/*                onClick={() => setSelectedColor(color)}*/}
                    {/*            />*/}
                    {/*        ))}*/}
                    {/*    </div>*/}
                    {/*</div>}*/}
                </div>
            </div>

            <div className="signature-buttons">
                <ButtonComponent variant={'outlined'} className="cancel-button"
                                 disabled={isSignatureEmpty()}
                                 handleClick={clearSignature}>Clear Signature</ButtonComponent>
                <ButtonComponent className="save-button"
                                 disabled={isSignatureEmpty()}
                                 handleClick={saveSignature}>Proceed</ButtonComponent>
            </div>
        </div>
    );
};

export default SignatureV2Component;
